/** Debug right-click context menu for in-game world manipulation: teleport, spawn, and tile editing. */
import Phaser from 'phaser';
import { DebugConfig } from './DebugConfig';
import { crispRoundedRect, crispText } from '../ui/CrispUtil';
import { Font, Hex, Color, ButtonColor } from '../ui/Theme';
import { worldToTile } from '../utils/Math';
import { DPR, CANVAS_SCALE, SF0_X, SF0_Y, GAME_WIDTH, GAME_HEIGHT } from '../utils/Constants';
import type { GameScene } from '../scenes/GameScene';
import type { EnemyManager } from '../systems/EnemyManager';
import type { Player } from '../entities/Player';

const MENU_DEPTH = 9999;
const BG_COLOR = Color.panelBg;
const BG_ALPHA = 0.9;
const ITEM_PAD_X = 12;
const ITEM_PAD_Y = 6;
const ITEM_HEIGHT = 20;
const CORNER_RADIUS = 4;

const ARCHETYPES = [
  'grunt', 'rusher', 'heavy', 'ranged',
  'shield', 'conductor', 'patrol', 'officer',
] as const;

interface TileOption {
  label: string;
  index: number;
}

const TILE_OPTIONS: TileOption[] = [
  { label: 'Floor',     index: 1 },
  { label: 'Floor Alt', index: 2 },
  { label: 'Wall',      index: 3 },
  { label: 'Empty',     index: 0 },
];

export class DebugContextMenu {
  private scene: Phaser.Scene;
  private player: Player;
  private enemyManager: EnemyManager;
  private groundLayer: Phaser.Tilemaps.TilemapLayer;

  private container: Phaser.GameObjects.Container | null = null;

  /** World coordinates of the right-click that spawned the current menu. */
  private clickWorldX = 0;
  private clickWorldY = 0;

  /**
   * Set during submenu transitions to prevent the scene-level left-click
   * dismiss handler from destroying the freshly-built submenu. Phaser fires
   * game-object pointerdown (the menu item click) before scene-level pointerdown
   * (the dismiss handler) in the same frame — without this guard the submenu
   * is born and killed in the same tick.
   */
  private skipDismiss = false;

  constructor(gameScene: GameScene, player: Player, enemyManager: EnemyManager, groundLayer: Phaser.Tilemaps.TilemapLayer) {
    this.scene = gameScene;
    this.player = player;
    this.enemyManager = enemyManager;
    this.groundLayer = groundLayer;

    // Suppress browser context menu on the game canvas
    gameScene.game.canvas.addEventListener('contextmenu', (e) => e.preventDefault());

    // Listen for right-click
    gameScene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!pointer.rightButtonDown()) return;
      if (!DebugConfig.flags.debugContextMenu) return;

      this.clickWorldX = pointer.worldX;
      this.clickWorldY = pointer.worldY;
      this.showMenu(pointer.x, pointer.y);
    });

    // Dismiss on left-click anywhere (fires after game-object handlers)
    gameScene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!pointer.leftButtonDown() || !this.container) return;
      if (this.skipDismiss) {
        this.skipDismiss = false;
        return;
      }
      this.dismiss();
    });

    // Dismiss on Escape
    if (gameScene.input.keyboard) {
      gameScene.input.keyboard.on('keydown-ESC', () => this.dismiss());
    }

    // Cleanup on scene shutdown
    gameScene.events.on('shutdown', () => this.dismiss());
  }

  // ---------------------------------------------------------------------------
  // Coordinate helpers
  // ---------------------------------------------------------------------------

  /**
   * Convert Phaser pointer coordinates to logical screen coordinates.
   * Phaser reports pointer.x in backing-store space (GAME_WIDTH * CANVAS_SCALE * DPR),
   * but scroll-factor(0) placement expects SF0_X + logical where logical ∈ [0, GAME_WIDTH].
   */
  private pointerToScreen(px: number, py: number): { sx: number; sy: number } {
    const scale = DPR * CANVAS_SCALE;
    return { sx: px / scale, sy: py / scale };
  }

  // ---------------------------------------------------------------------------
  // Menu rendering
  // ---------------------------------------------------------------------------

  private showMenu(pointerX: number, pointerY: number): void {
    this.dismiss();
    const { sx, sy } = this.pointerToScreen(pointerX, pointerY);
    this.buildMenu(sx, sy, [
      { label: 'Teleport Here', action: () => this.teleport() },
      { label: 'Spawn Enemy  \u25b8', action: () => this.openSubmenu(sx, sy, 'spawn') },
      { label: 'Change Tile  \u25b8', action: () => this.openSubmenu(sx, sy, 'tile') },
    ]);
  }

  private openSubmenu(screenX: number, screenY: number, kind: 'spawn' | 'tile'): void {
    this.skipDismiss = true;
    this.dismiss();
    if (kind === 'spawn') {
      const items = ARCHETYPES.map((id) => ({
        label: id.charAt(0).toUpperCase() + id.slice(1),
        action: () => this.spawnEnemy(id),
      }));
      this.buildMenu(screenX, screenY, items);
    } else {
      const items = TILE_OPTIONS.map((opt) => ({
        label: opt.label,
        action: () => this.changeTile(opt.index),
      }));
      this.buildMenu(screenX, screenY, items);
    }
  }

  private buildMenu(screenX: number, screenY: number, items: { label: string; action: () => void }[]): void {
    const container = this.scene.add.container(0, 0).setDepth(MENU_DEPTH).setScrollFactor(0);

    // Measure widest label to size the background
    const tempTexts = items.map((item) =>
      crispText(this.scene, 0, 0, item.label, { fontFamily: Font.family, fontSize: Font.sizeSmall, color: Hex.textPrimary }),
    );
    const maxWidth = Math.max(...tempTexts.map((t) => t.width));
    tempTexts.forEach((t) => t.destroy());

    const menuWidth = maxWidth + ITEM_PAD_X * 2;
    const menuHeight = items.length * ITEM_HEIGHT + ITEM_PAD_Y * 2;

    // Clamp to viewport so the menu never bleeds off-screen
    const clampedX = Math.min(screenX, GAME_WIDTH - menuWidth - 4);
    const clampedY = Math.min(screenY, GAME_HEIGHT - menuHeight - 4);

    // Offset for DPR scroll-factor(0) positioning
    const posX = SF0_X + clampedX;
    const posY = SF0_Y + clampedY;

    // Background panel
    const bg = crispRoundedRect(
      this.scene,
      posX + menuWidth / 2,
      posY + menuHeight / 2,
      menuWidth,
      menuHeight,
      CORNER_RADIUS,
      BG_COLOR,
      BG_ALPHA,
    ).setScrollFactor(0).setDepth(MENU_DEPTH);
    container.add(bg);

    // Menu items — each row has a full-width hit zone behind the label
    items.forEach((item, i) => {
      const itemY = posY + ITEM_PAD_Y + i * ITEM_HEIGHT + ITEM_HEIGHT / 2;

      // Invisible rectangle covering the full row width
      const hitZone = this.scene.add
        .rectangle(posX + menuWidth / 2, itemY, menuWidth, ITEM_HEIGHT, 0x000000, 0)
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(MENU_DEPTH + 1)
        .setInteractive({ useHandCursor: true });

      const text = crispText(
        this.scene,
        posX + ITEM_PAD_X,
        itemY,
        item.label,
        { fontFamily: Font.family, fontSize: Font.sizeSmall, color: Hex.textPrimary },
      )
        .setOrigin(0, 0.5)
        .setScrollFactor(0)
        .setDepth(MENU_DEPTH + 2);

      hitZone.on('pointerover', () => text.setColor(ButtonColor.debug.hover));
      hitZone.on('pointerout', () => text.setColor(Hex.textPrimary));
      hitZone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        if (!pointer.leftButtonDown()) return;
        item.action();
      });
      container.add(hitZone);
      container.add(text);
    });

    this.container = container;
  }

  private dismiss(): void {
    if (!this.container) return;
    this.container.each((child: Phaser.GameObjects.GameObject) => child.destroy());
    this.container.destroy();
    this.container = null;
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  private teleport(): void {
    const body = this.player.body as Phaser.Physics.Arcade.Body;
    body.reset(this.clickWorldX, this.clickWorldY);
    console.log(`[DebugContextMenu] Teleported to (${Math.round(this.clickWorldX)}, ${Math.round(this.clickWorldY)})`);
    this.dismiss();
  }

  private spawnEnemy(archetypeId: string): void {
    this.enemyManager.spawnAt(this.clickWorldX, this.clickWorldY, archetypeId);
    console.log(`[DebugContextMenu] Spawned ${archetypeId} at (${Math.round(this.clickWorldX)}, ${Math.round(this.clickWorldY)})`);
    this.dismiss();
  }

  private changeTile(tileIndex: number): void {
    const col = worldToTile(this.clickWorldX);
    const row = worldToTile(this.clickWorldY);

    // Update the live tilemap — removeTileAt for empty, putTileAt for everything else
    if (tileIndex === 0) {
      this.groundLayer.removeTileAt(col, row);
    } else {
      this.groundLayer.putTileAt(tileIndex, col, row);
      const tile = this.groundLayer.getTileAt(col, row);
      if (tile) {
        tile.setCollision(tileIndex !== 1 && tileIndex !== 2);
      }
    }

    console.log(`[DebugContextMenu] Set tile (${col}, ${row}) to GID ${tileIndex}`);

    // Persist only the single changed tile — never serialize the full layer from Phaser's
    // internal state, because Phaser's tile.index doesn't round-trip cleanly to Tiled GIDs
    // (empty tiles are stored as -1 internally but must be 0 in the JSON).
    const flatIndex = row * this.groundLayer.layer.width + col;
    fetch('/api/save-map', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ index: flatIndex, gid: tileIndex }),
    }).catch((err) => console.error('[DebugContextMenu] Failed to persist tile change:', err));

    this.dismiss();
  }
}
